import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { Store } from '@ngxs/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { InputsBindingsModel } from 'src/app/common/Models/Dialog/inputs-binding.model';
import { ICompanyInfo, IntositeLoginMethod } from 'src/app/common/Models/UI/company-info.interface';
import { ButtonInfo, ValueAndDisplay } from 'src/app/common/UI-Components/helperClasses/value-and-display.class';
import { Validators } from 'src/app/common/Validators/validators';
import { ServerApi } from 'src/app/services/api.services/server.api';
import { WebshareApiSvc } from 'src/app/services/api.services/webshare.api.svc';
import { DialogService } from 'src/app/services/dialogs.service';
import { MessagesBank } from 'src/app/services/status.service';
import { AppState } from 'src/app/Store/app.state/app.state';
import { BaseDialog } from '../../Dialog-types/base-dialog';
import { DialogRef } from '../../Dialog-types/dialog-ref';
import { NotificationDialogComponent } from '../notification-dialog/notification-dialog.component';
import { cloneDeep } from 'lodash';
import { PointCloudSvc } from 'src/app/services/api.services/point-cloud.service';
import { IWebShareConf, WebshareAuthType, WebshareLoadImageMethod } from 'src/app/common/Models/UI/point-cloud.interface';
import { MatTableDataSource, MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow } from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { NgClass, AsyncPipe } from '@angular/common';
import { TooltipDirective } from '../../../../Directives/tooltip.directive';
import { PerfectScrollbarModule } from 'perfect-scrollbar-angular';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { SelectionBoxComponent } from '../../../UI-Components/selection-box/selection-box.component';

@Component({
    selector: 'ins-webshare-conf-dialog',
    templateUrl: './webshare-conf-dialog.component.html',
    styleUrls: ['./webshare-conf-dialog.component.scss', './../shared-dialogs-ui.scss',
        './../../../UI-Components/shared-UI-components.scss', './../../../../Viewer/content.container/side-bar.container/subMenus/submenus-shared-design.scss'],
    imports: [MatIcon, FormsModule, TooltipDirective, PerfectScrollbarModule, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatFormField, MatInput, SelectionBoxComponent, NgClass, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, AsyncPipe]
})
export class WebshareConfDialogComponent extends BaseDialog implements OnInit {
  companyInfo$: Observable<ICompanyInfo>;
  public companyInfo: ICompanyInfo;
  public buttonsInfo: ButtonInfo[] = [];
  
  public confColumns: string[] = ['select', 'name', 'loginMethod', 'loadImageMethod', 'browserDomainUrl', 'domainUrl', 'apiKey'];
  public confDataSource: MatTableDataSource<any>;
  public showLoading: boolean = false;
  public noData;
  @ViewChild(MatSort, {static: true}) sort: MatSort;

  public selectedRowIndex = -1;
  public selectedConf: IWebShareConf;
  public webshareConfs: IWebShareConf[] = [];

  public filteredDataCount: number = 0;
  public searchStr: string = '';
  public pointCloudAuthPending: boolean = false;
  public webshareAuthTypes = WebshareAuthType;
  
  public webshareLoginMethods = [
    new ButtonInfo(WebshareAuthType.Manual, 'Manual'),
    new ButtonInfo(WebshareAuthType.Automatic, 'Automatic')
  ];

  public webshareLoadImageMethods = [
    new ButtonInfo(WebshareLoadImageMethod.Client, 'Client'),
    new ButtonInfo(WebshareLoadImageMethod.Server, 'Server'),
    new ButtonInfo(WebshareLoadImageMethod.Auto, 'Auto')
  ];

  // public selection = new SelectionModel<IWebShareConf>(true, []);
  
  private readonly dataChangedSub: Subject<string> = new Subject<string>();
  constructor(public injector: Injector, private readonly serverApi: ServerApi, private readonly pointCloudSvc: PointCloudSvc,
    public dialogService: DialogService, public validators: Validators, private readonly store: Store, private readonly webshareApiSvc: WebshareApiSvc) {
    super(injector);
    this.companyInfo$ = this.store.select(AppState.getCompanyInfo);
    this.buttonsInfo.push(
      new ButtonInfo('cancel', 'Cancel'),
      new ButtonInfo('save', 'Save'),
    );

    this.dataChangedSub.pipe(
      debounceTime(200))
      .subscribe(data => this.validateConfigurations());

    let companyInfo = this.store.selectSnapshot<ICompanyInfo>((state: any) => state.StateApp.companyInfo );
    if (companyInfo.intositeLoginMethod == IntositeLoginMethod.Enterprise) {
      this.webshareLoginMethods.push(new ButtonInfo(WebshareAuthType.Enterprise, 'Enterprise'));
    }
    if (companyInfo.intositeLoginMethod == IntositeLoginMethod.Sphere) {
      this.webshareLoginMethods.push(new ButtonInfo(WebshareAuthType.Sphere, 'Sphere'));
    }
  }

  public ngOnInit(): void {
    this.companyInfo$.subscribe((companyInfo: ICompanyInfo) => {
      this.companyInfo = companyInfo;
    });
    const webshareConfs = cloneDeep(this.pointCloudSvc.getAllPointCloudConfs());
    this.webshareConfs = [];
    webshareConfs.forEach((conf: IWebShareConf) => {
      if (conf.loginMethod == WebshareAuthType.Manual || conf.loginMethod == WebshareAuthType.Automatic) {
        conf.browserDomainUrl = conf.browserDomainUrl ? conf.browserDomainUrl : conf.domainUrl;
      }
      this.webshareConfs.push(conf);
    });
    this.loadDataInGrid();
  }

  public defineDialogModel(): void {}

  onChanges(): void {}

  public loadDataInGrid(): void {
    this.confDataSource = new MatTableDataSource(this.webshareConfs);
    this.confDataSource.sort = this.sort;
    this.noData = this.confDataSource.connect().pipe(map(data => data.length === 0));
    this.confDataSource.filterPredicate = function(data, filter: string): boolean {
      return data.name.toLowerCase().includes(filter);
    };
  }

  public isLogonMethodSelectEnabled(loginMethod: WebshareAuthType): boolean {
    if (loginMethod == WebshareAuthType.Enterprise && this.companyInfo.intositeLoginMethod != IntositeLoginMethod.Enterprise) {
      return false;
    }
    if (loginMethod == WebshareAuthType.Sphere && this.companyInfo.intositeLoginMethod != IntositeLoginMethod.Sphere) {
      return false;
    }
    return true;
  }

  public onRowUnselect() {
    this.selectedConf = null;
    this.selectedRowIndex = -1;
  }

  public onRowSelect(conf: IWebShareConf, index: number) {
    this.selectedRowIndex = index;
    this.selectedConf = conf;
  }

  public addNewRow() {
    this.webshareConfs.push({companyId: this.companyInfo.id, name: "", domainUrl: "", browserDomainUrl: "", apiKey: "", loginMethod: '', loadImageMethod: ''});
    this.loadDataInGrid();
  }

  public onRowClicked(conf: IWebShareConf, index: number, e: MouseEvent): void {
    if ( e.target['classList'].contains('mat-column-select') ) {
      if(this.selectedRowIndex != -1 && this.selectedRowIndex == index) {
        this.onRowUnselect();
      } else {
        this.onRowSelect(conf, index);
      }
    }
  }

  public removeRow() {
    if (this.selectedConf.id) {
      const inputsBinding: InputsBindingsModel = new Map([
        [ 'type', 'warning'],
        [ 'title', `Delete "${this.selectedConf.name}" Configuration` ],
        [ 'message', `Deleting configuration "${this.selectedConf.name}" will remove it from assigned sites. Are you sure you want to continue?`]
      ]);
      const dialog: DialogRef = this.dialogService.createNotificationDialog(inputsBinding);
      const dialogComp: NotificationDialogComponent = (dialog.instance as NotificationDialogComponent);
      dialogComp.buttonsInfo = [
        new ButtonInfo('cancel', 'Cancel'),
        new ButtonInfo('yes', 'Yes'),
      ];
      dialog.onClose$().subscribe((data) => {
        if (data.userAction === 'yes') {
          this.removeRowFromConfData();
        }
      });
    } else {
      this.removeRowFromConfData();
    }
  }

  public removeRowFromConfData(): void {
    this.webshareConfs.splice(this.selectedRowIndex, 1);
    this.loadDataInGrid();
    this.onRowUnselect();
    this.validateConfigurations();
  }

  public isConfRowDisabled(conf: IWebShareConf): boolean {
    return (conf.id && conf.loginMethod == WebshareAuthType.Enterprise &&
      this.companyInfo.intositeLoginMethod != IntositeLoginMethod.Enterprise) || (conf.id && conf.loginMethod == WebshareAuthType.Sphere &&
        this.companyInfo.intositeLoginMethod != IntositeLoginMethod.Sphere);
  }

  public getToolTipConf(element: any, label: string): any {
    let toolTipConf = {text: '', position: { H: 'hLeft', V:'vBottom' }, type: 'info'};
    if ( element.offsetWidth < element.scrollWidth )  {
      toolTipConf.text = label;
    }
    return toolTipConf;
  }

  public saveConfigurations(): void {
    this.webshareConfs.forEach((conf: IWebShareConf) => {
      conf.domainUrl = conf.domainUrl.trim().replace(/\/$/, "");
      conf.browserDomainUrl = conf.browserDomainUrl.trim().replace(/\/$/, "");
    });

    this.serverApi.statusBar.addNewStatus(MessagesBank.UPDATING_POINTCLOUD_CONFIGURATION);
    this.webshareApiSvc.updateWebhshareConf(this.companyInfo.id, this.webshareConfs)
      .subscribe({
        next: (res: any) => {
          if (res.hasErrors) {
            this.serverApi.createNotifiactionDialogForHttpCrisis(new Error(res.errorMessage), res.errorMessage);
          } else {
            this.webshareApiSvc.pointCloudConfsUpdated(res, false);
            this.close(this.buttonsInfo[1].value);
          }
          this.serverApi.statusBar.removeStatus(MessagesBank.UPDATING_POINTCLOUD_CONFIGURATION);
        },
        error: (err) => {
          this.serverApi.statusBar.removeStatus(MessagesBank.UPDATING_POINTCLOUD_CONFIGURATION);
          this.serverApi.createNotifiactionDialogForHttpCrisis(err, 'Error in updating Point Cloud Configurations. Please try later');
        }
      });
  }

  public clearSearchStr(): void {
    this.searchStr = '';
    this.searchStrChanged('');
  }

  public searchStrChanged(filterValue: string): void {
    this.confDataSource.filter = filterValue.trim().toLowerCase();
    this.filteredDataCount = this.confDataSource.filteredData.length;
  }

  public async testWebShareConnection(): Promise<void> {
    this.pointCloudSvc.testPointCloudConnection(this.selectedConf);
  }

  public showWebshareTestCon(): boolean {
    if (this.selectedConf) {
      return this.selectedConf.loginMethod == WebshareAuthType.Automatic && this.selectedConf.apiKey != '' && this.selectedConf.domainUrl != '';
    }
    return false;
  }

  public dataChanged(value: string): void {
    this.dataChangedSub.next(value);
  }

  public validateConfigurations(): void {
    let isConfValid = true;
    this.webshareConfs.forEach( (conf: IWebShareConf) => {
      switch(conf.loginMethod) {
        case WebshareAuthType.Automatic:
          if (!conf.name || !conf.domainUrl || !conf.browserDomainUrl || !conf.apiKey || !conf.loadImageMethod) {
            isConfValid = false;
          }
          break;
          case WebshareAuthType.Manual:
        case WebshareAuthType.Enterprise:
        case WebshareAuthType.Sphere:
          if (!conf.name || !conf.domainUrl || !conf.browserDomainUrl || !conf.loadImageMethod) {
            isConfValid = false;
          }
          break;
      }
      if ((conf.name || conf.domainUrl || conf.browserDomainUrl || conf.loadImageMethod) && !conf.loginMethod) {
        isConfValid = false;
      }
    });
    this.updateDialogValidity(isConfValid);
  }

  public updateDialogValidity(valid: boolean): void {
    this.isDialogValid = valid;
  }

  public closeDialog(param: any): void {
    this.webshareApiSvc.cancelCheckConnection();
    this.close(param);
  }

}
